import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  withStyles,
  CircularProgress,
  Typography,
  Button,
  Divider,
  Tooltip,
  IconButton,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardHeader,
  CardContent,
} from '@material-ui/core';
import {
  getApiKey,
  rollApiKey,
} from '@pingum/app-state-management/apiKey/actions';
import { showLogsModal } from '@pingum/app-state-management/log/actions';
import AuditLogsButton from '../../../widgets/AuditLogsButton';
import { getTenantFromUrl } from '../../../../utils/urlHelper';

export const styles = (theme) => ({
  blurryText: {
    color: 'transparent',
    textShadow: '0 0 10px rgba(0,0,0,0.5)',
  },
  smallText: { fontSize: 10 },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
});

export class ApiKeyCardComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showApiKey: false,
      openRollApiKeyDialog: false,
      expiration: 0,
    };

    this.props.getApiKey(getTenantFromUrl());
  }

  toggleKeyReveal = () => {
    this.setState((prevState) => ({ showApiKey: !prevState.showApiKey }));
  }

  handleExpirationChange = (e) => {
    this.setState({ expiration: e.target.value });
  }

  handleRollApiKeyDialogOpen = () => {
    this.setState({ openRollApiKeyDialog: true });
  }

  handleRollApiKeyDialogClose = () => {
    this.setState({ openRollApiKeyDialog: false });
  }

  handleRollApiKeyConfirm = () => {
    const expirationDate = new Date();
    expirationDate.setHours(this.state.expiration);
    this.props.rollApiKey(getTenantFromUrl(), expirationDate);
    this.setState({
      openRollApiKeyDialog: false,
      expiration: 0,
    });
  }

  showLogsModal = () => {
    this.props.showLogsModal({
      tenantName: getTenantFromUrl(),
      service: 'tenant',
      actions: ['New API Key Generated', 'API Key Rolled and Expired'],
    });
  }

  render() {
    return (
      <div>
        <Card>
          <CardHeader
            color="primary"
            title={<h4 className={this.props.classes.cardIconTitle}>Pingum API Key</h4>}
            subheader="Manage existing or add new users to your organization"
            action={<AuditLogsButton onClick={this.showLogsModal} />}
          />
          <CardContent>
            {
              this.props.isFetching
                ? (
                  <div>
                    <CircularProgress />
                  </div>
                )
                : (
                  <div>
                    <Typography variant="h6" gutterBottom>
                      Key
                      <Button color="primary" size="small" onClick={this.toggleKeyReveal} className={this.props.classes.smallText}>
                        {this.state.showApiKey
                          ? 'Hide API Key'
                          : 'Reveal API Key'}
                      </Button>
                      <Tooltip title="Roll API Key">
                        <IconButton onClick={this.handleRollApiKeyDialogOpen}>
                          <Icon>
                            cached
                          </Icon>
                        </IconButton>
                      </Tooltip>
                    </Typography>
                    {
                      this.state.showApiKey
                        ? (
                          <Typography variant="subtitle1" gutterBottom>
                            {this.props.apiKey.key}
                          </Typography>
                        )
                        : (
                          <Typography variant="subtitle1" gutterBottom className={this.props.classes.blurryText}>
                            0in4b7e078639dsd7f4b8bfb2mbi43b57
                          </Typography>
                        )
                    }
                    <Divider /> <br />
                    <Typography gutterBottom>
                      Information
                    </Typography>
                    <Typography variant="caption" gutterBottom>
                      {`
                      This key will give you access to utilize the Pingum APIs from your own system or application.
                      Be sure to keep this key safe and not to publish it to unwanted persons. The key has full access
                      to all roles (user, admin, owner, and developer). If you wish to retrieve a new API key,
                      you can roll this key by providing an expiration date from which the current key will no longer have access.
                      The expiration date provided is typically a date at which you plan to change over to the newly provided key
                      in your system or application. We will then provide you with a new API Key which can be used immediately.
                    `}
                    </Typography>

                    <Typography gutterBottom style={{ marginTop: 24 }}>
                      Request Headers
                    </Typography>
                    <Typography variant="caption" gutterBottom>
                      {`
                      To utilize the Pingum APIs with this API Key, you need to be aware of the required and optional
                      request headers which should be passed into each API Call.

                      you must provide two request headers so the
                      system knows which organization and authorization is making the request. The Authorization
                      header is simply just the API Key above. The X-Tenant header is the unique tenant identifier
                      for your organization.
                    `}
                    </Typography>
                    <Typography
                      component="div"
                      style={{
                        marginLeft: 12,
                        marginTop: 8,
                      }}
                      gutterBottom
                    >
                      <div>
                        <b>Authorization: </b>
                        {
                          this.state.showApiKey
                            ? this.props.apiKey.key
                            : (
                              <span className={this.props.classes.blurryText}>
                                0in4b7e078639dsd7f4b8bfb2mbi43b57
                              </span>
                            )
                        }
                      </div>
                      <Typography variant="caption">
                        (REQUIRED) The Authorization header is required for each API request.
                        By passing in your key in this header, Pingum will know which authorization is
                        making the request and insure only known and authorized entities can
                        invoke and access your organization&apos;s data.
                      </Typography>
                    </Typography>
                    <Typography
                      component="div"
                      style={{
                        marginLeft: 12,
                        marginTop: 16,
                      }}
                      gutterBottom
                    >
                      <div><b>X-Tenant: </b>{getTenantFromUrl()}</div>
                      <Typography variant="caption">
                        (REQUIRED) The X-Tenant header is required for each API request.
                        By passing in the tenant name, Pingum
                        can validate the passed in authorization header against this provided
                        tenant to ensure only known and authorized entities can access
                        your organization&apos;s data.
                      </Typography>
                    </Typography>
                    <Typography
                      component="div"
                      style={{
                        marginLeft: 12,
                        marginTop: 16,
                      }}
                      gutterBottom
                    >
                      <div><b>X-Timezone: </b>{Intl.DateTimeFormat().resolvedOptions().timeZone}</div>
                      <Typography variant="caption">
                        (OPTIONAL) The X-Timezone header is not required. If this header is missing,
                        Pingum will use UTC by default. This header is only used when displaying dates,
                        doing time based calculations, or searching on events based on a time range.
                      </Typography>
                    </Typography>
                  </div>
                )
              }
          </CardContent>
        </Card>
        <Dialog
          open={this.state.openRollApiKeyDialog}
          onClose={this.handleRollApiKeyDialogClose}
          aria-labelledby="role-api-key-title"
          aria-describedby="role-api-key-description"
          id="role-api-key-dialog"
        >
          <DialogTitle id="role-api-key-title">Roll API Key</DialogTitle>
          <DialogContent>
            <DialogContentText id="role-api-key-description">
              Are you sure you want to roll this API Key? After the expiration,
              you will be unable to perform any actions with the old key.
            </DialogContentText>
            <FormControl className={this.props.classes.formControl} fullWidth>
              <InputLabel htmlFor="age-simple">Expire Key</InputLabel>
              <Select
                value={this.state.expiration}
                onChange={this.handleExpirationChange}
                inputProps={{
                  name: 'expiration',
                  id: 'expiration-id',
                }}
              >
                <MenuItem value={0}>Immediately</MenuItem>
                <MenuItem value={1}>In 1 Hour</MenuItem>
                <MenuItem value={2}>In 2 Hours</MenuItem>
                <MenuItem value={4}>In 4 Hours</MenuItem>
                <MenuItem value={8}>In 8 Hours</MenuItem>
                <MenuItem value={12}>In 12 Hours</MenuItem>
                <MenuItem value={24}>In 24 Hours</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={this.handleRollApiKeyDialogClose}
              id="cancel-roll-api-key"
            >
              Cancel
            </Button>
            <Button
              onClick={this.handleRollApiKeyConfirm}
              color="primary"
              autoFocus
              id="confirm-roll-api-key"
            >
              Roll API Key
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

ApiKeyCardComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  isFetching: PropTypes.bool.isRequired,
  getApiKey: PropTypes.func.isRequired,
  rollApiKey: PropTypes.func.isRequired,
  apiKey: PropTypes.object.isRequired,
  showLogsModal: PropTypes.func.isRequired,
};

export const mapStateToProps = (state) => ({
  apiKey: state.apiKey.apiKey,
  isFetching: state.apiKey.isFetchingApiKey,
});

export const mapDispatchToProps = (dispatch) => ({
  getApiKey: (tenantName) => dispatch(getApiKey(tenantName)),
  rollApiKey: (tenantName, expirationDate) => dispatch(rollApiKey(tenantName, expirationDate)),
  showLogsModal: (request) => dispatch(showLogsModal(request)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(ApiKeyCardComponent));
